'use client'

import { Loading } from '@/components/ui/loading'
import { useMounted } from '@/hooks/use-mounted'
import { TONE_ACCENT_BAR } from '@/lib/tone'
import { selectImpersonationTransition } from '@/stores/authSlice'
import { useSession } from '@/utils/auth/auth-client'
import { useAppSelector } from '@/utils/redux-hooks'

import { useState } from 'react'

import { cn } from '@udecode/cn'
import { VenetianMask } from 'lucide-react'

import { useResetCacheOnIdentityChange } from '../hooks/use-reset-cache-on-identity-change'
import { EndImpersonationDialog } from './end-impersonation-dialog'

const FRAME_EDGE = cn('pointer-events-none fixed z-[100]', TONE_ACCENT_BAR.red)

/**
 * Impersonation chrome, mounted once in the root layout so it covers every
 * layout (apps, staff, super, Vue) without each one remembering to:
 *
 * - while impersonating, a red frame around the viewport and a small tab at
 *   the top saying whose view this is, that it is read-only, and how to leave;
 * - while a start or end is in flight, a full-screen "Switching to …" overlay
 *   until the page reloads as the new identity. It sits over the tree rather
 *   than replacing it, so the request that is mid-flight stays mounted;
 * - in every other tab, a cache reset when the identity changes under it.
 */
export function ImpersonationFrame() {
  const { data: session } = useSession()
  const transition = useAppSelector(selectImpersonationTransition)
  useResetCacheOnIdentityChange()
  const [endOpen, setEndOpen] = useState(false)
  // The client session can resolve on the first paint, which the server never
  // rendered; waiting for mount keeps hydration identical.
  const mounted = useMounted()

  const isImpersonating = mounted && !!session?.session?.impersonatedBy

  return (
    <>
      {transition && (
        <div className="bg-background fixed inset-0 z-[200] flex items-center justify-center">
          <Loading
            size="lg"
            message={
              transition.kind === 'start'
                ? `Switching to ${transition.name}…`
                : 'Returning to your account…'
            }
          />
        </div>
      )}
      {isImpersonating && (
        <>
          {/* Four edges, not one bordered box: the frame and the tab share one
              fill slot so they are the same red, and the tab reads as hanging
              off the top edge. */}
          <div aria-hidden className="pointer-events-none">
            <div className={cn(FRAME_EDGE, 'inset-x-0 top-0 h-0.5')} />
            <div className={cn(FRAME_EDGE, 'inset-x-0 bottom-0 h-0.5')} />
            <div className={cn(FRAME_EDGE, 'inset-y-0 left-0 w-0.5')} />
            <div className={cn(FRAME_EDGE, 'inset-y-0 right-0 w-0.5')} />
          </div>
          <div
            role="status"
            className={cn(
              'text-background fixed top-0 left-1/2 z-[101] flex -translate-x-1/2 items-center gap-1.5 rounded-b-md px-2.5 py-0.5 text-[11px] leading-4 font-medium whitespace-nowrap shadow-sm',
              TONE_ACCENT_BAR.red
            )}
          >
            <VenetianMask className="size-3 shrink-0" />
            <span className="max-w-[50vw] truncate">
              Viewing as {session?.user?.name}
            </span>
            <span aria-hidden>·</span>
            <span>Read-only</span>
            <button
              type="button"
              onClick={() => setEndOpen(true)}
              className="ml-1 underline underline-offset-2 hover:no-underline"
            >
              End
            </button>
          </div>
          <EndImpersonationDialog open={endOpen} onOpenChange={setEndOpen} />
        </>
      )}
    </>
  )
}
